import { useState, useEffect, useCallback } from 'react';
import { Select, message, Tag } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { CountryFlag } from './CountryFlag';
import eventsServiceInstance, { type EventWithCircuit } from '../../api/eventsService';
import { parseDateOnly, formatDateRange } from '../../utils/dateUtils';

interface EventSelectorProps {
  seasonYear?: number;
  value?: string; // composite key: season_year-circuit_id-start_date or older formats for backward compatibility
  onChange?: (event: EventWithCircuit) => void;
  style?: React.CSSProperties;
  placeholder?: string;
}

export const EventSelector: React.FC<EventSelectorProps> = ({
  seasonYear,
  value,
  onChange,
  style,
  placeholder = 'Select Event',
}) => {
  const [events, setEvents] = useState<EventWithCircuit[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEventKey, setSelectedEventKey] = useState<string | undefined>(value);

  const fetchEvents = useCallback(async () => {
    if (!seasonYear) {
      setEvents([]);
      setSelectedEventKey(undefined);
      return;
    }

    setLoading(true);
    try {
      const response = await eventsServiceInstance.getAllEvents(seasonYear);
      
      if (response.success && response.data && Array.isArray(response.data)) {
        const eventsData = response.data;
        setEvents(eventsData);
        
        // If there's a value and it exists in the new events, keep it selected
        // Support multiple key formats for backward compatibility
        if (value) {
          const matchingEvent = eventsData.find(e => {
            const fullKey = `${e.season_year}-${e.circuit_id}-${e.start_date}`;
            const oldCompositeKey = `${e.season_year}-${e.circuit_id}`;
            return fullKey === value || oldCompositeKey === value || e.circuit_id === value;
          });
          
          if (matchingEvent) {
            const fullKey = `${matchingEvent.season_year}-${matchingEvent.circuit_id}-${matchingEvent.start_date}`;
            setSelectedEventKey(fullKey);
            // Always update with fresh event data from the API
            // This ensures embedded data (sessions, stations, etc.) stays up to date
            // even when restored from localStorage cache
            onChange?.(matchingEvent);
          } else {
            // Stored value not found in current season, fallback to default selection
            if (eventsData.length > 0) {
              const defaultEvent = selectDefaultEvent(eventsData);
              const fullKey = `${defaultEvent.season_year}-${defaultEvent.circuit_id}-${defaultEvent.start_date}`;
              setSelectedEventKey(fullKey);
              onChange?.(defaultEvent);
            } else {
              setSelectedEventKey(undefined);
            }
          }
        } else if (eventsData.length > 0) {
          // Auto-select current or most recent event if no value is set
          const defaultEvent = selectDefaultEvent(eventsData);
          const fullKey = `${defaultEvent.season_year}-${defaultEvent.circuit_id}-${defaultEvent.start_date}`;
          setSelectedEventKey(fullKey);
          onChange?.(defaultEvent);
        } else {
          setSelectedEventKey(undefined);
        }
      } else {
        message.error('Failed to load events');
        setEvents([]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      message.error('Error loading events. Please check your connection.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [seasonYear, value, onChange]);

  // Fetch events when season year changes
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Update selected event when value prop changes
  useEffect(() => {
    if (value && events.length > 0) {
      const matchingEvent = events.find(e => {
        const fullKey = `${e.season_year}-${e.circuit_id}-${e.start_date}`;
        const oldCompositeKey = `${e.season_year}-${e.circuit_id}`;
        return fullKey === value || oldCompositeKey === value || e.circuit_id === value;
      });
      
      if (matchingEvent) {
        const fullKey = `${matchingEvent.season_year}-${matchingEvent.circuit_id}-${matchingEvent.start_date}`;
        setSelectedEventKey(fullKey);
      } else {
        setSelectedEventKey(value);
      }
    } else {
      setSelectedEventKey(value);
    }
  }, [value, events]);

  const handleChange = (eventKey: string) => {
    // Find event by composite key (including start_date for uniqueness)
    const event = events.find((e) => {
      const fullKey = `${e.season_year}-${e.circuit_id}-${e.start_date}`;
      return fullKey === eventKey;
    });
    
    if (event) {
      setSelectedEventKey(eventKey);
      onChange?.(event);
    }
  };

  // Helper function to select default event (current or most recent)
  const selectDefaultEvent = (eventsData: EventWithCircuit[]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // First, try to find an ongoing event
    let selectedEvent = eventsData.find(e => {
      const start = parseDateOnly(e.start_date);
      const end = parseDateOnly(e.end_date);
      end.setHours(23, 59, 59, 999);
      return today >= start && today <= end;
    });
    
    // If no ongoing event, select the most recent event
    if (!selectedEvent) {
      selectedEvent = eventsData.reduce((latest, current) => {
        const latestDate = parseDateOnly(latest.start_date);
        const currentDate = parseDateOnly(current.start_date);
        return currentDate > latestDate ? current : latest;
      }, eventsData[0]);
    }
    
    return selectedEvent;
  };

  return (
    <Select
      value={selectedEventKey}
      onChange={handleChange}
      loading={loading}
      placeholder={placeholder}
      disabled={!seasonYear || loading}
      style={{ minWidth: 280, ...style }}
      suffixIcon={<EnvironmentOutlined />}
      showSearch={{
        filterOption: (input, option) => {
          // Find the event by value to get the display name for filtering
          const event = events.find(e => 
            `${e.season_year}-${e.circuit_id}-${e.start_date}` === option?.value
          );
          return (event?.circuit.display_name.toLowerCase() ?? '').includes(input.toLowerCase());
        }
      }}
      options={events.map((event) => {
        const compositeKey = `${event.season_year}-${event.circuit_id}-${event.start_date}`;
        return {
          value: compositeKey,
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CountryFlag
                country={event.circuit.country}
                style={{
                  width: '18px',
                  height: '13px',
                  objectFit: 'cover',
                }}
              />
              <span>{event.circuit.display_name}</span>
              {event.status === 'Current' && (
                <Tag color="green" style={{ fontSize: '10px', lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                  Active
                </Tag>
              )}
              {event.demo && (
                <Tag color="orange" style={{ fontSize: '10px', lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                  Demo
                </Tag>
              )}
              {event.legacy && (
                <Tag color="default" style={{ fontSize: '10px', lineHeight: '16px', padding: '0 4px', margin: 0 }}>
                  Legacy
                </Tag>
              )}
              <span style={{ fontSize: '11px', color: '#888', marginLeft: 'auto' }}>
                {formatDateRange(event.start_date, event.end_date)}
              </span>
            </div>
          ),
        };
      })}
    />
  );
};

export default EventSelector;

